import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { KitOutProtocolWithItemsDto } from '@ctms/contracts';
import {
  AlertCircle,
  Boxes,
  Clock,
  History,
  Package,
  Plus,
  RefreshCw,
  Scale,
  ScrollText,
  Trash2,
} from 'lucide-react';

import { kitsApi } from '../kits.api.js';
import { kitQueryKeys } from '../kits.query-keys.js';
import { DestructionProtocolsDialog } from './destruction-protocols-dialog.js';
import { KitBasketDialog } from './kit-basket-dialog.js';
import { KitInventoryTable } from './kit-inventory-table.js';
import { ShipmentRegistrationDialog } from './shipment-registration-dialog.js';
import { ShipmentsHistoryDialog } from './shipments-history-dialog.js';
import { StockCorrectionsDialog } from './stock-corrections-dialog.js';

export interface KitInventoryViewProps {
  trialId: number;
  canWrite?: boolean;
  /** kits:write — gates virtual basket / protocol / correction write actions. */
  canWriteKits?: boolean;
  /**
   * documents:read — gates the A4 destruction-protocol print action inside the
   * register (issue #31 B1, REQ-DOC-018). Passed by the cockpit only with the
   * permission; `undefined` hides every print button (RBAC by omission).
   */
  onPrintProtocol?: (protocol: KitOutProtocolWithItemsDto, labName: string) => void;
}

export function KitInventoryView({
  trialId,
  canWrite = false,
  canWriteKits = false,
  onPrintProtocol,
}: KitInventoryViewProps): JSX.Element {
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isBasketOpen, setIsBasketOpen] = useState(false);
  const [isProtocolsOpen, setIsProtocolsOpen] = useState(false);
  const [isCorrectionsOpen, setIsCorrectionsOpen] = useState(false);

  const stockQuery = useQuery({
    queryKey: kitQueryKeys.stock.byTrial({ trialId }),
    queryFn: () => kitsApi.getStock({ trialId }),
    enabled: trialId > 0,
    staleTime: 60 * 1000,
  });

  const stockItems = stockQuery.data ?? [];

  // Summary metrics
  const totalStockPieces = stockItems.reduce((acc, kit) => acc + kit.totalStock, 0);
  const kitTypesCount = stockItems.length;
  const expiredBatchesCount = stockItems.reduce(
    (acc, kit) => acc + kit.batches.filter((b) => b.isExpired).length,
    0,
  );
  const expiringSoonCount = stockItems.reduce(
    (acc, kit) =>
      acc +
      kit.batches.filter(
        (b) => !b.isExpired && b.daysUntilExpiration !== null && b.daysUntilExpiration <= 30,
      ).length,
    0,
  );

  return (
    <div className="space-y-6" id="kit-inventory-view">
      {/* Top Header & Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800 pb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-100 flex items-center gap-2.5">
            <Boxes className="h-6 w-6 text-cyan-400" />
            <span>Magazyn zestawów laboratoryjnych</span>
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Ewidencja fizycznych materiałów, przyjmowanie paczek kurierskich oraz kolejka wydań FEFO
            (REQ-KIT-007, REQ-KIT-008, REQ-KIT-019, REQ-KIT-026)
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            id="refresh-stock-button"
            onClick={() => void stockQuery.refetch()}
            disabled={stockQuery.isFetching}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-300 hover:bg-slate-800 hover:text-slate-100 disabled:opacity-50"
            title="Odśwież stany"
          >
            <RefreshCw className={`h-4 w-4 ${stockQuery.isFetching ? 'animate-spin' : ''}`} />
            <span>Odśwież</span>
          </button>

          <button
            type="button"
            id="view-shipments-history-button"
            onClick={() => setIsHistoryOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800 px-3.5 py-2 text-xs font-medium text-slate-200 hover:bg-slate-700"
          >
            <History className="h-4 w-4 text-slate-400" />
            <span>Historia dostaw</span>
          </button>

          <button
            type="button"
            id="view-basket-button"
            onClick={() => setIsBasketOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800 px-3.5 py-2 text-xs font-medium text-slate-200 hover:bg-slate-700"
          >
            <Trash2 className="h-4 w-4 text-rose-400" />
            <span>Kosz wirtualny</span>
          </button>

          <button
            type="button"
            id="view-protocols-button"
            onClick={() => setIsProtocolsOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800 px-3.5 py-2 text-xs font-medium text-slate-200 hover:bg-slate-700"
          >
            <ScrollText className="h-4 w-4 text-slate-400" />
            <span>Protokoły zniszczenia</span>
          </button>

          <button
            type="button"
            id="view-corrections-button"
            onClick={() => setIsCorrectionsOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800 px-3.5 py-2 text-xs font-medium text-slate-200 hover:bg-slate-700"
          >
            <Scale className="h-4 w-4 text-amber-400" />
            <span>Korekty stanu</span>
          </button>

          {canWrite && (
            <button
              type="button"
              id="register-shipment-button"
              onClick={() => setIsRegisterOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-xs font-semibold text-white shadow-md hover:bg-cyan-500 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Przyjmij przesyłkę</span>
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards Bar */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total Stock */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 shadow-sm flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-950/60 border border-emerald-800/40 text-emerald-400">
            <Package className="h-6 w-6" />
          </div>
          <div>
            <span className="text-xs font-medium text-slate-400">Dostępne zestawy</span>
            <div className="text-2xl font-bold font-mono text-slate-100 mt-0.5">
              {totalStockPieces} <span className="text-sm font-normal text-slate-400">szt.</span>
            </div>
          </div>
        </div>

        {/* Catalog Kit Types */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 shadow-sm flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-cyan-950/60 border border-cyan-800/40 text-cyan-400">
            <Boxes className="h-6 w-6" />
          </div>
          <div>
            <span className="text-xs font-medium text-slate-400">Rodzaje zestawów</span>
            <div className="text-2xl font-bold font-mono text-slate-100 mt-0.5">
              {kitTypesCount} <span className="text-sm font-normal text-slate-400">typów</span>
            </div>
          </div>
        </div>

        {/* Expired Batches Warning (REQ-KIT-026) */}
        <div
          className={`rounded-xl border p-4 shadow-sm flex items-center gap-4 ${
            expiredBatchesCount > 0
              ? 'border-rose-900/80 bg-rose-950/30 text-rose-200'
              : 'border-slate-800 bg-slate-900/60 text-slate-400'
          }`}
        >
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg border ${
              expiredBatchesCount > 0
                ? 'bg-rose-950 border-rose-800/80 text-rose-400'
                : 'bg-slate-800/50 border-slate-700/50 text-slate-500'
            }`}
          >
            <AlertCircle className="h-6 w-6" />
          </div>
          <div>
            <span className="text-xs font-medium">Partie przeterminowane</span>
            <div className="text-2xl font-bold font-mono text-slate-100 mt-0.5">
              {expiredBatchesCount} <span className="text-sm font-normal text-slate-400">partii</span>
            </div>
          </div>
        </div>

        {/* Expiring Soon */}
        <div
          className={`rounded-xl border p-4 shadow-sm flex items-center gap-4 ${
            expiringSoonCount > 0
              ? 'border-amber-900/80 bg-amber-950/30 text-amber-200'
              : 'border-slate-800 bg-slate-900/60 text-slate-400'
          }`}
        >
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg border ${
              expiringSoonCount > 0
                ? 'bg-amber-950 border-amber-800/80 text-amber-400'
                : 'bg-slate-800/50 border-slate-700/50 text-slate-500'
            }`}
          >
            <Clock className="h-6 w-6" />
          </div>
          <div>
            <span className="text-xs font-medium">Wygasają w 30 dni</span>
            <div className="text-2xl font-bold font-mono text-slate-100 mt-0.5">
              {expiringSoonCount} <span className="text-sm font-normal text-slate-400">partii</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Stock Table */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-200">
            Aktualny stan magazynowy zestawów
          </h2>
          <span className="text-xs text-slate-500">
            Stan wyliczany w czasie rzeczywistym z przyjętych dostaw
          </span>
        </div>

        <KitInventoryTable stockItems={stockItems} isLoading={stockQuery.isLoading} />
      </div>

      {/* Modals */}
      <ShipmentRegistrationDialog
        trialId={trialId}
        open={isRegisterOpen}
        onOpenChange={setIsRegisterOpen}
      />

      <ShipmentsHistoryDialog
        trialId={trialId}
        open={isHistoryOpen}
        onOpenChange={setIsHistoryOpen}
      />

      <KitBasketDialog
        trialId={trialId}
        canWrite={canWriteKits}
        open={isBasketOpen}
        onOpenChange={setIsBasketOpen}
      />

      <DestructionProtocolsDialog
        trialId={trialId}
        open={isProtocolsOpen}
        onOpenChange={setIsProtocolsOpen}
        onPrint={onPrintProtocol}
      />

      <StockCorrectionsDialog
        trialId={trialId}
        canWrite={canWriteKits}
        open={isCorrectionsOpen}
        onOpenChange={setIsCorrectionsOpen}
      />
    </div>
  );
}
